// filepath: /Users/shaunscott/Dev Projects/shaunscott_com/src/utils/webVitals.ts
import { onCLS, onFID, onLCP, onFCP, onTTFB, Metric } from 'web-vitals';
import ReactGA from 'react-ga4';

/**
 * Send web vitals metrics to Google Analytics
 * @param metric - The web vitals metric object
 */
const sendToGoogleAnalytics = (metric: Metric): void => {
  // Destructure the metric object
  const { name, value, id, delta } = metric;
  
  // Log vitals in development for debugging
  if (import.meta.env.DEV) {
    console.log(`Web Vitals: ${name}`, {
      name,
      value: Math.round(name === 'CLS' ? value * 1000 : value), // Convert CLS to milliseconds for easier comparison
      delta,
      id
    });
  }

  // Create a suitable label for the metric including the URL and metric ID
  const label = `${window.location.pathname}${window.location.search} - ${id}`;
  
  // Round the metric value and classify it
  const roundedValue = Math.round(name === 'CLS' ? value * 1000 : value); // Convert CLS to milliseconds
  let rating = 'unknown';
  
  // Use Google's Core Web Vitals thresholds for classification
  // https://web.dev/vitals/
  switch (name) {
    case 'CLS':
      rating = value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
      break;
    case 'FID':
      rating = value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
      break;
    case 'LCP':
      rating = value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
      break;
    case 'FCP':
      rating = value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
      break;
    case 'TTFB':
      rating = value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor';
      break;
    default:
      break;
  }

  // Send to Google Analytics as an event
  ReactGA.event({
    category: 'Web Vitals',
    action: name,
    label: `${label} - ${rating}`,
    value: roundedValue,
    nonInteraction: true,
    transport: 'beacon'
  });
};

/**
 * Initialize all Web Vitals reporting
 */
export const reportWebVitals = (): void => {
  // Report Core Web Vitals
  onCLS(sendToGoogleAnalytics);  // Cumulative Layout Shift
  onFID(sendToGoogleAnalytics);  // First Input Delay
  onLCP(sendToGoogleAnalytics);  // Largest Contentful Paint
  
  // Additional metrics
  onFCP(sendToGoogleAnalytics);  // First Contentful Paint
  onTTFB(sendToGoogleAnalytics); // Time to First Byte
};

/**
 * Get the current performance metrics as an object
 * For immediate use in components that need current values
 */
export const getPerformanceMetrics = async (): Promise<Record<string, number> | null> => {
  try {
    // Use modern Performance API if available
    if (typeof performance.getEntriesByType === 'function') {
      const metrics: Record<string, number> = {};
      const navEntries = performance.getEntriesByType('navigation');
      
      if (navEntries.length > 0) {
        const nav = navEntries[0] as PerformanceNavigationTiming;
        metrics.pageLoadTime = nav.duration;
        metrics.ttfb = nav.responseStart;
        metrics.domContentLoaded = nav.domContentLoadedEventEnd - nav.fetchStart;
      }
      
      // Get paint metrics
      const paintEntries = performance.getEntriesByType('paint');
      paintEntries.forEach(entry => {
        if (entry.name === 'first-paint') {
          metrics.firstPaint = entry.startTime;
        }
        if (entry.name === 'first-contentful-paint') {
          metrics.firstContentfulPaint = entry.startTime;
        }
      });
      
      return metrics;
    }
    
    // Fallback to older API
    if (window.performance && (window.performance as any).timing) {
      const timing = (window.performance as any).timing;
      return {
        pageLoadTime: timing.loadEventEnd - timing.navigationStart,
        ttfb: timing.responseStart - timing.navigationStart,
        domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting performance metrics:', error);
    return null;
  }
};
